import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import {
  Activity, 
  History,
  Users, 
  Shield, 
  Wallet,
  Settings,
  FileText,
  ClipboardList
} from 'lucide-static';
import './Sidebar.css';
const avisaLogo = `${process.env.PUBLIC_URL}/images/avisa.svg`;

// Icon wrapper component to safely render SVG strings
const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    
    if (size) {
      svgString = svgString.replace('<svg', `<svg width="${size}" height="${size}"`);
    }
    
    if (color) {
      svgString = svgString.replace('<svg', `<svg color="${color}" stroke="${color}"`);
    }
    
    if (className) {
      svgString = svgString.replace('<svg', `<svg class="${className}"`);
    }
    
    return svgString;
  };

  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSystemAdmin } = useAuth();
  const { } = useData(); // allDocuments, etc if needed later

  const getActiveNav = () => {
    const path = location.pathname;
    
    // System Admin paths
    if (path === '/system-admin/dashboard' || path === '/system-admin/overview') return 'overview';
    if (path === '/system-admin/user-management') return 'users';
    if (path === '/system-admin/documents') return 'documents';
    if (path === '/system-admin/requests') return 'requests';
    if (path === '/system-admin/logs') return 'logs';
    if (path === '/system-admin/purchases') return 'purchases';
    if (path === '/system-admin/security') return 'security';
    if (path === '/system-admin/settings') return 'settings';
    
    // Regular Admin paths
    if (path === '/dashboard' || path === '/overview') return 'overview';
    if (path === '/user-management') return 'users';
    if (path === '/documents') return 'documents';
    if (path === '/settings') return 'settings';
    
    return '';
  };

  const activeNav = getActiveNav();

  const navItems = isSystemAdmin() 
    ? [
        { id: 'overview', path: '/system-admin/dashboard', icon: Activity, label: 'Overview' },
        { id: 'users', path: '/system-admin/user-management', icon: Users, label: 'User Management' },
        { id: 'documents', path: '/system-admin/documents', icon: FileText, label: 'Document Management' },
        { id: 'requests', path: '/system-admin/requests', icon: ClipboardList, label: 'Requests' },
        { id: 'purchases', path: '/system-admin/purchases', icon: Wallet, label: 'Purchases' },
        { id: 'logs', path: '/system-admin/logs', icon: History, label: 'Logs' },
        { id: 'security', path: '/system-admin/security', icon: Shield, label: 'Security' },
        { id: 'settings', path: '/system-admin/settings', icon: Settings, label: 'Settings' },
      ]
    : [
        { id: 'overview', path: '/dashboard', icon: Activity, label: 'Overview' },
        { id: 'users', path: '/user-management', icon: Users, label: 'User Management' },
        { id: 'documents', path: '/documents', icon: FileText, label: 'Document Management' },
        { id: 'settings', path: '/settings', icon: Settings, label: 'Settings' },
      ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-content">
        <div className="sidebar-header">
          <img src={avisaLogo} alt="AVISA Logo" className="sidebar-logo-image" />
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const isActive = activeNav === item.id;
            return (
              <a
                key={item.id}
                href={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation(item.path);
                }}
              >
                {isActive && (
                  <motion.div
                    layoutId="sidebar-active-background"
                    className="nav-active-indicator"
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }}
                    aria-hidden="true"
                  />
                )}
                <Icon icon={item.icon} size={20} className="nav-icon" />
                <span className="nav-label">{item.label}</span>
              </a>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="copyright">
            © 2026 AVISA. All rights reserved.
            <div className="sidebar-version">
              {process.env.REACT_APP_VERSION || 'v1.0.0-rc.2'}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
